import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ILoginApiResponse } from '../account/account.model';

export interface SessionData {
  token: string;
  user: ILoginApiResponse;
  isAuthenticated: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private sessionDataSubject = new BehaviorSubject<SessionData | null>(null);
  public sessionData$ = this.sessionDataSubject.asObservable();

  constructor() {
    this.initializeSession();
  }

  private initializeSession(): void {
    const token = this.getCookie('auth_token');
    const userData = this.getCookie('user_data');
    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        this.sessionDataSubject.next({
          token,
          user,
          isAuthenticated: true
        });
      } catch (error) {
        console.error('Error parsing user data from cookie:', error);
        this.clearSession();
      }
    }
  }

  setSession(token: string, user: ILoginApiResponse, rememberMe: boolean): void {
    // Set cookies with 7 or 14 days expiration
    const expirationDate = new Date();
    let days = 7;
    expirationDate.setDate(expirationDate.getDate() + (rememberMe ? days * 2 : days));
    this.setCookie('auth_token', token, expirationDate);
    this.setCookie('user_data', JSON.stringify(user), expirationDate);
    this.sessionDataSubject.next({
      token,
      user,
      isAuthenticated: true
    });
  }

  clearSession(): void {
    this.deleteCookie('auth_token');
    this.deleteCookie('user_data');
    this.sessionDataSubject.next(null);
  }

  getCurrentToken(): string | null {
    return this.getCookie('auth_token');
  }

  getCurrentUser(): ILoginApiResponse | null {
    const userData = this.getCookie('user_data');
    if (userData) {
      try {
        return JSON.parse(userData) as ILoginApiResponse;
      } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
      }
    }
    return null;
  }

  isAuthenticated(): boolean {
    const token = this.getCurrentToken();
    return !!token;
  }

  // Cookie utility methods
  private setCookie(name: string, value: string, expirationDate: Date): void {
    document.cookie = `${name}=${value}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Strict`;
  }

  private getCookie(name: string): string | null {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  private deleteCookie(name: string): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }
}
